/**
 * Module: `export/pdf-emoji.ts` — monochrome emoji font support for pdfmake.
 * Roboto (default vfs) has no emoji glyphs; Noto Emoji fills that gap.
 */

import {
  EXPORT_EMOJI_FONT_PDF,
  EXPORT_SANS_FONT_PDF,
} from "@/lib/export/export-styles";
import notoEmojiUrl from "@/assets/fonts/NotoEmoji-Regular.ttf?url";

export { EXPORT_EMOJI_FONT_PDF };

/** Emoji grapheme clusters (pictographs + optional VS16 / ZWJ chains, flags). */
const EMOJI_SEQUENCE_REGEX =
  /(?:\p{Extended_Pictographic}(?:\uFE0F)?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F)?)*)|\p{Regional_Indicator}{2}/gu;

export type PdfEmojiTextSpan =
  | string
  | {
      text: string | PdfEmojiTextSpan[];
      font?: string;
      bold?: boolean;
      italics?: boolean;
      link?: string;
      decoration?: "underline";
      fontSize?: number;
      color?: string;
      style?: string;
    };

/**
 * Splits plain text so emoji runs use the NotoEmoji font family.
 */
export function splitPdfTextWithEmoji(text: string): PdfEmojiTextSpan[] {
  if (!text) return [];

  const spans: PdfEmojiTextSpan[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(EMOJI_SEQUENCE_REGEX)) {
    const emoji = match[0] ?? "";
    const index = match.index ?? 0;
    if (index > lastIndex) {
      spans.push(text.slice(lastIndex, index));
    }
    spans.push({
      text: emoji,
      font: EXPORT_EMOJI_FONT_PDF,
    });
    lastIndex = index + emoji.length;
  }
  if (lastIndex < text.length) {
    spans.push(text.slice(lastIndex));
  }
  return spans.length > 0 ? spans : [text];
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

async function loadEmojiFontBase64(): Promise<string> {
  try {
    const response = await fetch(notoEmojiUrl);
    if (response.ok) {
      return arrayBufferToBase64(await response.arrayBuffer());
    }
  } catch {
    // Fall through to filesystem (Vitest / Node).
  }

  const { existsSync, readFileSync } = await import("node:fs");
  const { resolve } = await import("node:path");
  const candidates = [
    resolve(process.cwd(), "src/assets/fonts/NotoEmoji-Regular.ttf"),
  ];
  try {
    const { fileURLToPath } = await import("node:url");
    candidates.push(
      fileURLToPath(
        new URL("../../assets/fonts/NotoEmoji-Regular.ttf", import.meta.url)
      )
    );
  } catch {
    // import.meta.url may not be a file: URL under Vitest.
  }

  const fontPath = candidates.find((candidate) => existsSync(candidate));
  if (!fontPath) {
    throw new Error(
      `NotoEmoji font not found (tried: ${candidates.join(", ")})`
    );
  }
  return readFileSync(fontPath).toString("base64");
}

type PdfMakeWithFonts = {
  addVirtualFileSystem: (vfs: Record<string, string>) => void;
  addFonts?: (fonts: Record<string, Record<string, string>>) => void;
  fonts?: Record<string, Record<string, string>>;
};

let emojiFontReady: Promise<boolean> | null = null;

/**
 * Registers Noto Emoji into pdfmake vfs + font map (idempotent).
 * Returns false if the font file cannot be loaded (PDF still works without emoji).
 */
export async function ensurePdfEmojiFont(
  pdfMake: PdfMakeWithFonts
): Promise<boolean> {
  if (!emojiFontReady) {
    emojiFontReady = (async () => {
      try {
        const fileName = "NotoEmoji-Regular.ttf";
        const base64 = await loadEmojiFontBase64();
        pdfMake.addVirtualFileSystem({ [fileName]: base64 });

        const fontDef = {
          [EXPORT_EMOJI_FONT_PDF]: {
            normal: fileName,
            bold: fileName,
            italics: fileName,
            bolditalics: fileName,
          },
          // Keep Roboto explicit when we touch the font registry.
          [EXPORT_SANS_FONT_PDF]: {
            normal: "Roboto-Regular.ttf",
            bold: "Roboto-Medium.ttf",
            italics: "Roboto-Italic.ttf",
            bolditalics: "Roboto-MediumItalic.ttf",
          },
        };

        if (typeof pdfMake.addFonts === "function") {
          pdfMake.addFonts(fontDef);
        } else {
          pdfMake.fonts = { ...(pdfMake.fonts ?? {}), ...fontDef };
        }

        if (!pdfMake.fonts?.[EXPORT_EMOJI_FONT_PDF]?.normal) {
          throw new Error("NotoEmoji font registration did not stick");
        }
        return true;
      } catch (error) {
        console.warn("[export-pdf] Noto Emoji font unavailable", error);
        return false;
      }
    })();
  }
  return emojiFontReady;
}

/** Whether the emoji font was successfully registered for this runtime. */
export async function isPdfEmojiFontReady(
  pdfMake: PdfMakeWithFonts
): Promise<boolean> {
  return ensurePdfEmojiFont(pdfMake);
}

/** Test helper: clear cached registration between runs. */
export function resetPdfEmojiFontCacheForTests(): void {
  emojiFontReady = null;
}
